import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  Chip,
  Stack,
  Typography,
  Paper,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from "@mui/material";
import { Add } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { isValidUrl, isValidCveNota, reorder } from "../../utils/validators";
import { useGenerateClipping } from "../../hooks/useClipping/usePostClipping";

export const GenerateClippingPanel = ({ token }) => {
  const [downloadQueue, setDownloadQueue] = useState([]);

  const addToDownloadQueue = (item) => {
    setDownloadQueue((prev) => [...prev, item]);
  };

  const { generateClipping } = useGenerateClipping(
    token,
    addToDownloadQueue
  );

  const [nombreDocumento, setNombreDocumento] = useState("");
  const [mode, setMode] = useState("url");

  const [urls, setUrls] = useState([]);
  const [urlInput, setUrlInput] = useState("");

  const [cveNotas, setCveNotas] = useState([]);
  const [cveInput, setCveInput] = useState("");

  const [sectionsInput, setSectionsInput] = useState([
    {
      seccion_nombre: "",
      cveNotas: [],
      input: "",
    },
  ]);

  const [coverFile, setCoverFile] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);

  const [fecha, setFecha] = useState("");

  const [config, setConfig] = useState({
    cover: false,
    index: false,
    dividers: false,
  });

  const [writeDate, setWriteDate] = useState({
    cover: false,
    index: false,
  });

  const handleModeChange = (_, value) => {
    setMode(value);
    setUrls([]);
    setCveNotas([]);
    setSectionsInput([{ seccion_nombre: "", cveNotas: [] }]);
    setUrlInput("");
    setCveInput("");
  };

  const addUrl = () => {
    if (!isValidUrl(urlInput)) return;
    setUrls((prev) => [...prev, { url: urlInput }]);
    setUrlInput("");
  };

  const addCve = () => {
    const value = cveInput.trim();

    if (!isValidCveNota(value)) return;

    setCveNotas((prev) => [...prev, Number(value)]);
    setCveInput("");
  };

  const addSection = () => {
    setSectionsInput((prev) => [
      ...prev,
      { seccion_nombre: "", cveNotas: [], input: "" },
    ]);
  };

  const removeSection = (index) => {
    setSectionsInput((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const updateSectionName = (index, value) => {
    const updated = [...sectionsInput];
    updated[index].seccion_nombre = value;
    setSectionsInput(updated);
  };

  const updateSectionInput = (index, value) => {
    const updated = [...sectionsInput];
    updated[index].input = value;
    setSectionsInput(updated);
  };

  const addCveToSection = (index) => {
    const value = sectionsInput[index].input;

    if (!isValidCveNota(value)) return;

    const updated = [...sectionsInput];
    updated[index].cveNotas.push(Number(value));
    updated[index].input = "";

    setSectionsInput(updated);
  };

  const removeCveFromSection = (sectionIndex, cveIndex) => {
    const updated = [...sectionsInput];
    updated[sectionIndex].cveNotas.splice(cveIndex, 1);
    setSectionsInput(updated);
  };

  const onDragStart = (index) => setDragIndex(index);

  const onDrop = (index, list, setter) => {
    if (dragIndex === null) return;
    setter(reorder(list, dragIndex, index));
    setDragIndex(null);
  };

  const handleGenerate = () => {
    let baseBody = {
      nombre_documento: nombreDocumento,
    };

    if (mode === "url") {
      baseBody.listWitness = urls;
    }

    if (mode === "cve") {
      baseBody.cveNotas = cveNotas;
    }

    if (mode === "sections") {
      baseBody.sectionsInput = sectionsInput;
    }

    if (coverFile) {
      baseBody.coverFile = coverFile;
    }

    const body = {
      ...baseBody,
      ...(fecha && { fecha }),
      config,
      writeDate,
      coverFile,
    };

    generateClipping(body);
  };

  const handleConfigChange = (field) => (event) => {
    const value = event.target.checked;

    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (!value) {
      setWriteDate((prev) => ({
        ...prev,
        [field]: false,
      }));
    }
  };

  const handleWriteDateChange = (field) => {
    setWriteDate({
      cover: field === "cover",
      index: field === "index",
    });
  };

  const disabled =
    !nombreDocumento ||
    (mode === "url" && urls.length === 0) ||
    (mode === "cve" && cveNotas.length === 0) ||
    (mode === "sections" &&
      sectionsInput.some(
        (s) => !s.seccion_nombre || s.cveNotas.length === 0
      ));

  const renderDraggableChip = (label, index, list, setter) => (
    <Chip
      key={`${label}-${index}`}
      label={label}
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={() => onDrop(index, list, setter)}
      onDelete={() => setter(list.filter((_, i) => i !== index))}
      sx={{ cursor: "grab" }}
    />
  );

  return (
    <Paper sx={{ height: "100%", p: 3, borderRadius: 2, overflowY: "auto" }}>
      <Typography variant="h6" mb={2}>
        Generar Clipping PDF
      </Typography>

      <TextField
        label="Nombre del documento"
        fullWidth
        size="small"
        value={nombreDocumento}
        onChange={(e) => setNombreDocumento(e.target.value)}
        sx={{ mb: 2 }}
      />

      <Tabs value={mode} onChange={handleModeChange} sx={{ mb: 2 }}>
        <Tab label="Por URL" value="url" />
        <Tab label="Por Clave de Nota" value="cve" />
        <Tab label="Por Secciones" value="sections" />
      </Tabs>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Configuración avanzada
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={2}>
            <Typography variant="subtitle2" mb={1}>
              Configuración del documento
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Switch
                    checked={config.cover}
                    onChange={handleConfigChange("cover")}
                  />
                }
                label="Portada"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.index}
                    onChange={handleConfigChange("index")}
                  />
                }
                label="Índice"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={config.dividers}
                    onChange={(e) =>
                      setConfig({ ...config, dividers: e.target.checked })
                    }
                  />
                }
                label="Separadores"
              />
            </Stack>
          </Box>
          {(config.cover || config.index) && (
            <Box mb={2}>
              <Typography variant="subtitle2" mb={1}>
                Mostrar fecha
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                {config.cover && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={writeDate.cover}
                        onChange={() => handleWriteDateChange("cover")}
                      />
                    }
                    label="En portada"
                  />
                )}
                {config.index && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={writeDate.index}
                        onChange={() => handleWriteDateChange("index")}
                      />
                    }
                    label="En índice"
                  />
                )}
              </Stack>
            </Box>
          )}
          {config.cover && (
            <Stack spacing={1} mb={2}>
              <Button variant="outlined" component="label">
                Subir portada (PDF)
                <input
                  type="file"
                  hidden
                  accept="application/pdf"
                  onChange={(e) => {
                    const file = e.target.files[0];

                    if (file && file.type !== "application/pdf") {
                      alert("Solo se permiten archivos PDF");
                      return;
                    }

                    setCoverFile(file);
                  }}
                />
              </Button>

              {coverFile && (
                <Typography variant="caption">
                  Archivo cargado: {coverFile.name}
                </Typography>
              )}
            </Stack>
          )}
          <TextField
            label="Fecha (opcional)"
            type="date"
            size="small"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </AccordionDetails>
      </Accordion>

      {mode === "url" && (
        <>
          <Stack direction="row" spacing={1} mb={2}>
            <TextField
              label="URL del testigo"
              size="small"
              fullWidth
              value={urlInput}
              error={!!urlInput && !isValidUrl(urlInput)}
              helperText={
                urlInput && !isValidUrl(urlInput) ? "URL inválida" : ""
              }
              onChange={(e) => setUrlInput(e.target.value)}
            />
            <Button onClick={addUrl} variant="contained">
              <Add />
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {urls.map((u, i) =>
              renderDraggableChip(u.url, i, urls, setUrls)
            )}
          </Stack>
        </>
      )}

      {mode === "cve" && (
        <>
          <Stack direction="row" spacing={1} mb={2}>
            <TextField
              label="Clave de nota"
              size="small"
              value={cveInput}
              error={!!cveInput && !isValidCveNota(cveInput)}
              helperText={
                cveInput && !isValidCveNota(cveInput)
                  ? "Solo números"
                  : ""
              }
              onChange={(e) => setCveInput(e.target.value)}
            />
            <Button onClick={addCve} variant="contained">
              <Add />
            </Button>
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            {cveNotas.map((cve, i) =>
              renderDraggableChip(`cveNota: ${cve}`, i, cveNotas, setCveNotas)
            )}
          </Stack>
        </>
      )}

      {mode === "sections" && (
        <>
          <Button onClick={addSection} variant="outlined" sx={{ mb: 2 }}>
            Agregar sección
          </Button>

          {sectionsInput.map((section, index) => (
            <Paper key={index} sx={{ p: 2, mb: 2 }}>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography variant="subtitle2">
                  Sección {index + 1}
                </Typography>

                <Button
                  color="error"
                  size="small"
                  onClick={() => removeSection(index)}
                >
                  Eliminar
                </Button>
              </Box>
              
              <TextField
                label="Nombre de la sección"
                fullWidth
                size="small"
                value={section.seccion_nombre}
                onChange={(e) =>
                  updateSectionName(index, e.target.value)
                }
                sx={{ mb: 2 }}
              />

              <Stack direction="row" spacing={1} mb={2}>
                <TextField
                  label="Clave de nota"
                  size="small"
                  value={section.input}
                  onChange={(e) =>
                    updateSectionInput(index, e.target.value)
                  }
                />
                <Button onClick={() => addCveToSection(index)} variant="contained">
                  <Add />
                </Button>
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                {section.cveNotas.map((cve, i) => (
                  <Chip
                    key={i}
                    label={`cveNota: ${cve}`}
                    onDelete={() => removeCveFromSection(index, i)}
                  />
                ))}
              </Stack>
            </Paper>
          ))}
        </>
      )}

      <Box textAlign="right" mt={3}>
        <Button
          variant="contained"
          disabled={disabled}
          onClick={handleGenerate}
        >
          Generar PDF
        </Button>
      </Box>

      {downloadQueue.length > 0 && (
        <Box mt={3}>
          <Typography variant="subtitle1">
            Descargas
          </Typography>

          <Stack spacing={1} mt={1}>
            {downloadQueue.map((item, index) => (
              <Paper
                key={index}
                variant="outlined"
                sx={{ p: 1, display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="body2">
                  {item.nameDocument}
                </Typography>

                <Button
                  size="small"
                  component="a"
                  href={item.url}
                  download={item.nameDocument}
                >
                  Descargar
                </Button>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Paper>
  );
};
